import React, { FC, Fragment, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ImmutableTrip } from 'src/types/trips/Trips';
import { open } from 'src/common/ducks/zippy';
import {
  CONFIRMED_DATE_TRIP_STATES,
  FORMS,
  PREFERRED_DATE_FIELD,
  REQUESTED_CHANGES_WARNING,
  SUGGESTED_DATES_FIELD,
  UNSENT_CHANGES_WARNING,
} from 'src/inbox/constants';
import {
  getOtherParty,
  modified as tripModified,
  userIsRenter,
} from 'src/inbox/helpers';
import DateTime from 'src/inbox/components/DateTime';
import DateTimeSnippet from 'src/common/components/DateTimeSnippet';
import NotificationMessage from 'src/common/components/NotificationMessage';
import EditButton from 'src/inbox/components/EditButton';
import Modal from 'src/common/components/Modal';
import Card from 'src/inbox/components/presentation/Card';
import { MOMENT_FORMAT } from 'src/common/constants';
import cardStyles from 'src/inbox/components/presentation/Card/Card.module.scss';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import { List } from 'immutable';
import DatesForm from './form';
import styles from './DatesCard.module.scss';

type DatesCardProps = {
  trip: ImmutableTrip;
  readOnly?: boolean;
  requestedChanges?: boolean;
  checkTripModification?: boolean;
  withBookingMotivators?: boolean;
};

/**
 * Import multiple css modules into a component
 *
 * Usual card consist of several independent components. To be able
 * to tie these components via CSS to show EditButton on hover we
 * should combine styles from 2 components.
 */
const s = {
  ...cardStyles,
  ...styles,
};

export const getSingleChosenDateLabel = (
  trip: ImmutableTrip,
  preferredDate: string,
) => {
  const suggestedDates = trip.get(SUGGESTED_DATES_FIELD, List());
  const newDateSelected = !suggestedDates.includes(preferredDate);
  const isPreferredDateSelected = preferredDate === suggestedDates.get(0);
  const isRenter = userIsRenter(trip);
  const renterName = trip.getIn(['renter', 'first_name']);

  let result = '';
  if (isRenter) {
    result = 'Suggested date:';
    if (isPreferredDateSelected) {
      result = 'Preferred date:';
    } else if (!newDateSelected) {
      result = 'Alternate date:';
    }
  } else {
    result = 'Suggested date:';
    if (isPreferredDateSelected) {
      result = `${renterName}’s preferred date:`;
    } else if (!newDateSelected) {
      result = `${renterName}’s alternate date:`;
    }
  }

  return result;
};

export const shouldRenderSingleChosenDate = (offerIsModified: boolean, tripState: string) => {
  const isConfirmedDateTripState = CONFIRMED_DATE_TRIP_STATES.includes(tripState);
  return offerIsModified || isConfirmedDateTripState;
};

const DatesCard: FC<DatesCardProps> = ({
  trip,
  readOnly,
  checkTripModification,
  requestedChanges,
  withBookingMotivators = false,
}) => {
  const dispatch = useDispatch();
  const openModal = useCallback(() => dispatch(open(FORMS.DATES)), [dispatch]);

  const {
    offerTrip: {
      startDate: offerTripStartDate,
      endDate: offerTripEndDate,
      suggestedDatesWithEnds,
      isOfferDatesModified,
    },
  } = useSelector(dateTimeDurationSelector(trip.get('pk')));

  const tripIsModified = Boolean(
    checkTripModification && tripModified(trip, [PREFERRED_DATE_FIELD]),
  );
  const modified = !!(isOfferDatesModified || tripIsModified);
  const isRenter = userIsRenter(trip);
  const otherUser = getOtherParty(trip);
  const otherName = otherUser.get('first_name');

  const shouldRenderChosenDate = shouldRenderSingleChosenDate(isOfferDatesModified, trip.getIn(['state', 'state']));
  const chosenDateLabel = getSingleChosenDateLabel(trip, offerTripEndDate);

  return (
    <Card
      highlighted={modified}
      classNameModifier={!readOnly ? 'hoverEffect' : undefined}
      withGap
      withoutMarginBottom
      withDashedBorderBottom
    >
      {/* These should probably be injected with a `children` prop. */}
      {isOfferDatesModified && (
        <NotificationMessage outdent={false}>
          {UNSENT_CHANGES_WARNING}
        </NotificationMessage>
      )}
      {requestedChanges && tripIsModified && !isOfferDatesModified && (
        <NotificationMessage outdent={false}>
          {REQUESTED_CHANGES_WARNING}
        </NotificationMessage>
      )}
      {shouldRenderChosenDate && (
        <>
          <span className={s.label}>{chosenDateLabel}</span>
          <DateTimeSnippet>
            <DateTime
              departDateTime={moment(offerTripStartDate).format(MOMENT_FORMAT.DATE)}
              returnDateTime={moment(offerTripEndDate).format(MOMENT_FORMAT.DATE)}
              classNameModifier={modified ? 'modified' : ''}
            />
          </DateTimeSnippet>
        </>
      )}

      {!shouldRenderChosenDate && suggestedDatesWithEnds.map((suggestedDate, index) => (
        // The order of these dates are not likely to change
        // so it should be okay to use array indices
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {index === 1 && suggestedDatesWithEnds.length > 1 && (
            <span className={s.label}>
              {`Alternate date${suggestedDatesWithEnds.length > 2 ? 's' : ''}`}
            </span>
          )}
          <DateTimeSnippet>
            <DateTime
              departDateTime={moment(suggestedDate.preferred_date)}
              returnDateTime={moment(suggestedDate.end_date)}
              withSmallLabels={index >= 1}
            />
          </DateTimeSnippet>
        </Fragment>
      ))}

      {/* This could also be injected. */}
      {!readOnly && (
        <>
          <EditButton openModal={openModal} />
          <Modal
            name="dates"
            label="Dates Form"
          >
            <DatesForm
              withBookingMotivators={withBookingMotivators}
              firstName={otherName}
              isRenter={isRenter}
              trip={trip}
            />
          </Modal>
        </>
      )}
    </Card>
  );
};

export default DatesCard;
